import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Folder, ArrowLeft, Send, RefreshCcw, Info, ChevronRight, Home, CheckCheck, Loader2, CheckCircle2, XCircle, X, Images, Video } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import DriveThumbnail from "@/components/DriveThumbnail";

const VIDEO_BATCH_CAP = 5;
const CAROUSEL_MIN = 2;
const CAROUSEL_MAX = 10; // Instagram caps at 10; TikTok up to 35 — cap at 10 for cross-platform safety

function humanSize(bytes) {
  if (!bytes) return "";
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const INITIAL_PROGRESS = { active: false, total: 0, done: 0, current: null, results: [], mode: "video" };

export default function DriveBrowser() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get("mode") === "carousel" ? "carousel" : "video";
  const isCarousel = mode === "carousel";

  const [driveOk, setDriveOk] = useState(false);
  const [driveConn, setDriveConn] = useState(null);
  const [upCfg, setUpCfg] = useState(null);
  const [stack, setStack] = useState([]);
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});
  const [caption, setCaption] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState("tiktok");
  const [sentMap, setSentMap] = useState({});
  const [progress, setProgress] = useState(INITIAL_PROGRESS);
  const [autoMusic, setAutoMusic] = useState(false);

  const accounts = upCfg?.accounts || [];
  // For the selected platform, only accounts that have it linked are usable.
  const linkedAccounts = useMemo(
    () => accounts.filter((a) => (selectedPlatform === "tiktok" ? a.tiktok_connected : a.instagram_connected)),
    [accounts, selectedPlatform]
  );
  const anyLinked = accounts.some((a) => a.tiktok_connected || a.instagram_connected);
  const anyTikTok = accounts.some((a) => a.tiktok_connected);
  const anyInstagram = accounts.some((a) => a.instagram_connected);
  const rootFolderName = driveConn?.root_folder_name;
  const rootLabel = rootFolderName || "My Drive";
  const current = stack[stack.length - 1] || null;
  const currentId = current?.id || null;

  const selectedFiles = useMemo(() => Object.values(selectedMap), [selectedMap]);
  const selectedIds = useMemo(() => Object.keys(selectedMap), [selectedMap]);
  const selectedCount = selectedFiles.length;
  const sending = progress.active;

  const canSend = useMemo(() => {
    if (!driveOk || !upCfg?.configured || !anyLinked) return false;
    if (!selectedAccountId) return false;
    if (sending) return false;
    if (isCarousel) return selectedCount >= CAROUSEL_MIN && selectedCount <= CAROUSEL_MAX;
    return selectedCount > 0 && selectedCount <= VIDEO_BATCH_CAP;
  }, [driveOk, upCfg, anyLinked, selectedAccountId, sending, isCarousel, selectedCount]);

  const toggleSelect = (file) => {
    setSelectedMap((m) => {
      const next = { ...m };
      if (next[file.id]) {
        delete next[file.id];
        return next;
      }
      // Enforce caps at selection time so the user gets immediate feedback
      const currentCount = Object.keys(next).length;
      if (isCarousel && currentCount >= CAROUSEL_MAX) {
        toast.warning(`Carousels can hold at most ${CAROUSEL_MAX} photos`);
        return m;
      }
      if (!isCarousel && currentCount >= VIDEO_BATCH_CAP) {
        toast.warning(`Up to ${VIDEO_BATCH_CAP} videos per batch — send this batch first or deselect one`);
        return m;
      }
      next[file.id] = file;
      return next;
    });
  };

  const clearSelection = () => setSelectedMap({});
  const removeFromSelection = (id) => setSelectedMap((m) => { const n = { ...m }; delete n[id]; return n; });

  const selectAllInFolder = () => {
    setSelectedMap((m) => {
      const next = { ...m };
      const cap = isCarousel ? CAROUSEL_MAX : VIDEO_BATCH_CAP;
      for (const f of files) {
        if (Object.keys(next).length >= cap) break;
        next[f.id] = f;
      }
      return next;
    });
  };

  const loadFolders = async (parentId) => {
    setFoldersLoading(true);
    try {
      const { data } = await api.get("/drive/folders", { params: parentId ? { parentId } : {} });
      setFolders(data.folders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to list folders");
    } finally {
      setFoldersLoading(false);
    }
  };

  const loadFiles = async (folderId) => {
    setFilesLoading(true);
    try {
      const { data } = await api.get("/drive/files", { params: { folderId: folderId || undefined, kind: isCarousel ? "image" : "video" } });
      setFiles(data.files || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to list files");
    } finally {
      setFilesLoading(false);
    }
  };

  const enterFolder = async (folder) => {
    setStack((s) => [...s, { id: folder.id, name: folder.name }]);
    await Promise.all([loadFolders(folder.id), loadFiles(folder.id)]);
  };

  const goRoot = async () => {
    setStack([]);
    await Promise.all([loadFolders(null), loadFiles(null)]);
  };

  const goToCrumb = async (i) => {
    const next = stack.slice(0, i + 1);
    setStack(next);
    const target = next[next.length - 1];
    await Promise.all([loadFolders(target ? target.id : null), loadFiles(target ? target.id : null)]);
  };

  const loadSent = async () => {
    try {
      const { data } = await api.get("/attempts/sent-file-ids");
      const m = {};
      (data.sent || []).forEach((s) => { m[s.drive_file_id] = s; });
      setSentMap(m);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      const [d, u] = await Promise.all([
        api.get("/drive/status"),
        api.get("/integrations/uploadpost"),
      ]);
      setDriveOk(d.data.connected);
      setDriveConn(d.data.connection);
      setUpCfg(u.data);
      // Pick a sensible default platform + account: prefer TikTok if any linked
      const list = u.data.accounts || [];
      const firstTT = list.find((a) => a.tiktok_connected);
      const firstIG = list.find((a) => a.instagram_connected);
      if (firstTT) { setSelectedPlatform("tiktok"); setSelectedAccountId(firstTT.id); }
      else if (firstIG) { setSelectedPlatform("instagram"); setSelectedAccountId(firstIG.id); }
      if (d.data.connected) {
        await Promise.all([loadFolders(null), loadSent()]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload files whenever the mode changes (video ↔ carousel) and drive is ready
  useEffect(() => {
    setSelectedMap({});
    if (driveOk) loadFiles(currentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, driveOk]);

  // When platform changes, ensure the selected account is one that has that platform linked
  useEffect(() => {
    if (linkedAccounts.length === 0) { setSelectedAccountId(null); return; }
    if (!linkedAccounts.find((a) => a.id === selectedAccountId)) {
      setSelectedAccountId(linkedAccounts[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, upCfg]);

  const chosenAccount = linkedAccounts.find((a) => a.id === selectedAccountId);

  const sendVideos = async () => {
    const items = selectedFiles;
    setProgress({ active: true, total: items.length, done: 0, current: items[0], results: [], mode: "video" });
    for (let i = 0; i < items.length; i++) {
      const f = items[i];
      setProgress((p) => ({ ...p, current: f, done: i }));
      let result;
      try {
        const { data } = await api.post("/draft/send", {
          drive_file_id: f.id,
          account_id: selectedAccountId,
          caption: caption || null,
          platform: selectedPlatform,
        });
        result = {
          file: f,
          status: data.status === "success" ? "success" : "failure",
          summary: data.response_summary || (data.status === "success" ? "Sent" : "Failed"),
        };
      } catch (e) {
        if (e?.isPaywall) {
          toast.error(e?.response?.data?.detail || "Daily limit reached", {
            action: { label: "Upgrade", onClick: () => navigate("/pricing") },
            duration: 8000,
          });
          setProgress((p) => ({ ...p, active: false, done: p.total, current: null }));
          return; // stop the batch — no point trying more items on the same quota
        }
        result = { file: f, status: "failure", summary: e?.response?.data?.detail || "Send failed" };
      }
      setProgress((p) => ({ ...p, results: [...p.results, result] }));
    }
    setProgress((p) => ({ ...p, active: false, done: p.total, current: null }));
    const successFiles = new Set(items.filter((_, i) => {
      // We don't have direct access to results here in closure — simply clear all attempted on success later
      return true;
    }));
    void successFiles; // no-op
    await loadSent();
  };

  const sendCarousel = async () => {
    const items = selectedFiles;
    setProgress({ active: true, total: 1, done: 0, current: items[0], results: [], mode: "carousel" });
    let result;
    try {
      const { data } = await api.post("/draft/send_carousel", {
        drive_file_ids: items.map((f) => f.id),
        account_id: selectedAccountId,
        caption: caption || null,
        platform: selectedPlatform,
        tiktok_auto_add_music: selectedPlatform === "tiktok" ? autoMusic : false,
        tiktok_photo_cover_index: 0,
      });
      result = {
        file: { id: "carousel", name: `${items.length}-photo ${selectedPlatform === "tiktok" ? "slideshow" : "carousel"}` },
        status: data.status === "success" ? "success" : "failure",
        summary: data.response_summary || (data.status === "success" ? "Posted" : "Failed"),
      };
    } catch (e) {
      if (e?.isPaywall) {
        toast.error(e?.response?.data?.detail || "Daily limit reached", {
          action: { label: "Upgrade", onClick: () => navigate("/pricing") },
          duration: 8000,
        });
        setProgress(INITIAL_PROGRESS);
        return;
      }
      result = {
        file: { id: "carousel", name: "Carousel" },
        status: "failure",
        summary: e?.response?.data?.detail || "Send failed",
      };
    }
    setProgress((p) => ({ ...p, results: [result], active: false, done: 1, current: null }));
    await loadSent();
  };

  const dismissProgress = () => {
    // Clear selection for successfully-posted files
    const successIds = new Set(progress.results.filter((r) => r.status === "success").flatMap((r) => (r.file.id === "carousel" ? selectedIds : [r.file.id])));
    if (successIds.size > 0) {
      setSelectedMap((m) => {
        const next = { ...m };
        for (const id of successIds) delete next[id];
        return next;
      });
    }
    setProgress(INITIAL_PROGRESS);
  };

  const notReady = !driveOk || !upCfg?.configured || !anyLinked;
  if (notReady) {
    return (
      <div className="space-y-6" data-testid="drive-blocked">
        <header>
          <h1 className="font-display font-black tracking-tight text-4xl text-slate-900">Drive Browser</h1>
          <p className="mt-2 text-slate-600">Complete integrations first.</p>
        </header>
        <Card className="p-8 border-zinc-200 flex items-center gap-6">
          <img
            alt=""
            src="https://images.unsplash.com/photo-1519219788971-8d9797e0928e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG9yZ2FuaXplZCUyMGRlc2slMjBlbXB0eXxlbnwwfHx8fDE3ODQyMDAwNzR8MA&ixlib=rb-4.1.0&q=85"
            className="w-40 h-28 object-cover rounded-md hidden md:block"
          />
          <div>
            <div className="font-display font-bold text-xl text-slate-900">Missing integration</div>
            <ul className="mt-2 text-sm text-slate-600 list-disc pl-5">
              {!driveOk && <li>Google Drive is not connected.</li>}
              {!upCfg?.configured && <li>Publishing key is not saved.</li>}
              {upCfg?.configured && !anyLinked && <li>No TikTok or Instagram accounts are linked yet.</li>}
            </ul>
            <Link to="/integrations">
              <Button className="mt-4 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="go-integrations">
                Open Integrations
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="drive-browser-page">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5">
            {isCarousel ? <><Images className="w-3 h-3" /> Carousel</> : <><Video className="w-3 h-3" /> Video</>}
          </div>
          <h1 className="mt-1 font-display font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl text-slate-900">
            {isCarousel ? "Pick photos" : "Pick a video"}
          </h1>
          <p className="mt-1.5 text-sm sm:text-base text-slate-600">
            {isCarousel
              ? <>Tap {CAROUSEL_MIN}–{CAROUSEL_MAX} photos in the order you want them shown.</>
              : <>Tap up to {VIDEO_BATCH_CAP} videos — we&apos;ll send them one after another.</>}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => Promise.all([loadFolders(currentId), loadFiles(currentId)])}
          data-testid="refresh-button"
          className="shrink-0"
        >
          <RefreshCcw className="w-4 h-4" /><span className="hidden sm:inline">Refresh</span>
        </Button>
      </header>

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 text-xs sm:text-sm text-slate-600 flex-wrap" data-testid="breadcrumbs">
        <button className="inline-flex items-center gap-1 hover:text-slate-900 transition-colors" onClick={goRoot} data-testid="crumb-root">
          <Home className="w-3.5 h-3.5" /> {rootLabel}
        </button>
        {stack.map((s, i) => (
          <span key={s.id} className="inline-flex items-center gap-1">
            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            <button
              className="hover:text-slate-900 truncate max-w-[140px] sm:max-w-[240px] transition-colors"
              onClick={() => goToCrumb(i)}
              title={s.name}
              data-testid={`crumb-${s.id}`}
            >
              {s.name}
            </button>
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-zinc-200 rounded-lg overflow-hidden">
        {/* Folders pane */}
        <div className="lg:col-span-3 border-b lg:border-b-0 lg:border-r border-zinc-200 bg-white" data-testid="folders-pane">
          <div className="px-4 py-2.5 border-b border-zinc-200 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
            Subfolders
          </div>
          <div className="max-h-[32vh] lg:max-h-[60vh] overflow-y-auto">
            {foldersLoading ? (
              <div className="p-4 text-sm text-slate-500">Loading folders…</div>
            ) : folders.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No subfolders here.</div>
            ) : (
              folders.map((f) => (
                <button
                  key={f.id}
                  onClick={() => enterFolder(f)}
                  className="w-full text-left flex items-center gap-2 px-4 py-3 text-sm text-slate-700 hover:bg-zinc-100 hover:text-slate-900 transition-colors border-b border-zinc-50 last:border-b-0"
                  data-testid={`folder-item-${f.id}`}
                >
                  <Folder className="w-4 h-4 shrink-0 text-slate-500" />
                  <span className="truncate flex-1">{f.name}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Files pane */}
        <div className="lg:col-span-6 border-b lg:border-b-0 lg:border-r border-zinc-200 bg-white" data-testid="files-pane">
          <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 flex-1 truncate">
              {isCarousel ? "Photos" : "Videos"} in {current ? `“${current.name}”` : rootLabel}
            </div>
            {files.length > 0 && (
              <button
                type="button"
                onClick={selectAllInFolder}
                className="text-[10px] sm:text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                data-testid="select-all-in-folder"
              >
                Select up to {isCarousel ? CAROUSEL_MAX : VIDEO_BATCH_CAP}
              </button>
            )}
            <Badge variant="secondary" className="bg-zinc-100 text-zinc-700 text-[10px]">
              {isCarousel ? "Photos only" : "Videos only"}
            </Badge>
          </div>
          <div className="max-h-[52vh] lg:max-h-[68vh] overflow-y-auto p-3">
            {filesLoading ? (
              <div className="p-4 text-sm text-slate-500">Loading files…</div>
            ) : files.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">
                No {isCarousel ? "photos" : "videos"} in this folder.
              </div>
            ) : (
              <div className={`grid ${isCarousel ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-2"} gap-3`}>
                {files.map((f) => {
                  const orderIndex = selectedIds.indexOf(f.id);
                  const selected = orderIndex >= 0;
                  const sent = sentMap[f.id];
                  return (
                    <button
                      key={f.id}
                      onClick={() => toggleSelect(f)}
                      className={`group flex flex-col text-left rounded-lg overflow-hidden bg-white transition-all ${
                        selected
                          ? "ring-2 ring-blue-500 shadow-md"
                          : "ring-1 ring-zinc-200 hover:ring-zinc-300 hover:shadow-sm"
                      }`}
                      data-testid={`file-item-${f.id}`}
                      aria-pressed={selected}
                    >
                      <div className={`relative w-full ${isCarousel ? "aspect-square" : "aspect-[9/16]"} bg-zinc-100`}>
                        <DriveThumbnail
                          fileId={f.id}
                          hasThumbnail={f.hasThumbnail !== false}
                          name={f.name}
                          className="absolute inset-0 w-full h-full rounded-none"
                          size={640}
                        />
                        {sent && !selected && (
                          <div
                            className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-semibold px-2 py-0.5 shadow"
                            title={`Posted ${new Date(sent.created_at).toLocaleString()}`}
                            data-testid={`posted-badge-${f.id}`}
                          >
                            <CheckCheck className="w-3 h-3" /> Posted
                          </div>
                        )}
                        {selected && (
                          <div className="absolute top-2 left-2" data-testid={`order-badge-${f.id}`}>
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold shadow-md ring-2 ring-white">
                              {orderIndex + 1}
                            </span>
                          </div>
                        )}
                        {isCarousel && selected && orderIndex === 0 && (
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-fuchsia-600 text-white hover:bg-fuchsia-600 text-[10px] shadow" data-testid={`cover-badge-${f.id}`}>Cover</Badge>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-2">
                          <div className="text-[11px] text-white font-medium truncate" title={f.name}>{f.name}</div>
                        </div>
                      </div>
                      <div className="px-2 py-1.5 text-[10px] text-slate-500 tabular-nums truncate">
                        {f.size ? humanSize(f.size) : (f.mimeType || "").replace(/^(video|image)\//, "")}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Details / send pane (desktop) */}
        <div className="hidden lg:block lg:col-span-3 bg-white" data-testid="details-pane">
          <div className="px-4 py-2.5 border-b border-zinc-200 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center justify-between">
            <span>{isCarousel ? "Post carousel" : "Post video"}</span>
            {selectedCount > 0 && (
              <Badge className="bg-blue-600 text-white hover:bg-blue-600 text-[10px]" data-testid="selected-count-desktop">
                {selectedCount} selected
              </Badge>
            )}
          </div>
          <div className="p-4 space-y-4">
            {selectedCount === 0 ? (
              <div className="text-sm text-slate-500" data-testid="empty-selection-hint">
                Tap {isCarousel ? "photos in order — tap #1 first" : "one or more videos"} to send.
              </div>
            ) : (
              <SendForm
                selectedFiles={selectedFiles}
                caption={caption}
                setCaption={setCaption}
                humanSize={humanSize}
                onRemove={removeFromSelection}
                onClearAll={clearSelection}
                isCarousel={isCarousel}
                selectedPlatform={selectedPlatform}
                setSelectedPlatform={setSelectedPlatform}
                anyTikTok={anyTikTok}
                anyInstagram={anyInstagram}
                linkedAccounts={linkedAccounts}
                selectedAccountId={selectedAccountId}
                setSelectedAccountId={setSelectedAccountId}
                autoMusic={autoMusic}
                setAutoMusic={setAutoMusic}
              />
            )}
            <Button
              onClick={isCarousel ? sendCarousel : sendVideos}
              disabled={!canSend}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
              data-testid="send-draft-button"
            >
              <Send className="w-4 h-4" />
              {isCarousel
                ? `Post ${selectedCount}-photo ${selectedPlatform === "tiktok" ? "slideshow" : "carousel"}`
                : (selectedCount > 1 ? `Send ${selectedCount} videos` : "Send video")}
            </Button>
            {current && (
              <Button variant="ghost" onClick={() => goToCrumb(stack.length - 2)} className="w-full text-slate-600" data-testid="up-one-level">
                <ArrowLeft className="w-4 h-4" /> Up one level
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sticky send bar */}
      {selectedCount > 0 && (
        <div
          className="lg:hidden fixed left-0 right-0 z-30 bg-white border-t border-zinc-200 shadow-[0_-4px_16px_-6px_rgba(0,0,0,0.08)]"
          style={{ bottom: "calc(64px + env(safe-area-inset-bottom, 0px))" }}
          data-testid="mobile-send-bar"
        >
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-start gap-2 min-w-0">
              <DriveThumbnail
                fileId={selectedFiles[0].id}
                hasThumbnail={selectedFiles[0].hasThumbnail !== false}
                name={selectedFiles[0].name}
                className="w-14 h-10"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate" data-testid="selected-file-name">
                  {selectedCount === 1 ? selectedFiles[0].name : `${selectedCount} ${isCarousel ? "photos" : "videos"} queued`}
                </div>
                <div className="text-xs text-slate-500 tabular-nums">
                  {isCarousel ? `Order matches tap order · #1 is the cover` : `Sent one after another`}
                </div>
              </div>
              <button className="text-xs text-slate-500 shrink-0" onClick={clearSelection} data-testid="clear-selection-mobile">Clear</button>
            </div>
            <PlatformAndAccountPicker
              compact
              selectedPlatform={selectedPlatform}
              setSelectedPlatform={setSelectedPlatform}
              anyTikTok={anyTikTok}
              anyInstagram={anyInstagram}
              linkedAccounts={linkedAccounts}
              selectedAccountId={selectedAccountId}
              setSelectedAccountId={setSelectedAccountId}
            />
            <Button
              onClick={isCarousel ? sendCarousel : sendVideos}
              disabled={!canSend}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
              data-testid="send-draft-button-mobile"
            >
              <Send className="w-4 h-4" />
              {isCarousel
                ? `Post ${selectedCount}-photo ${selectedPlatform === "tiktok" ? "slideshow" : "carousel"}`
                : (selectedCount > 1 ? `Send ${selectedCount} → ${chosenAccount?.label || "?"}` : `Send → ${chosenAccount?.label || "?"}`)}
            </Button>
          </div>
        </div>
      )}

      <SendProgressBar progress={progress} onDismiss={dismissProgress} platform={selectedPlatform} />
    </div>
  );
}

function SendForm({
  selectedFiles, caption, setCaption, humanSize,
  onRemove, onClearAll, isCarousel, selectedPlatform, setSelectedPlatform,
  anyTikTok, anyInstagram, linkedAccounts, selectedAccountId, setSelectedAccountId,
  autoMusic, setAutoMusic,
}) {
  const count = selectedFiles.length;
  return (
    <>
      <div>
        <div className="text-xs text-slate-500 mb-2 flex items-center justify-between">
          <span>{count === 1 ? "File" : `${count} ${isCarousel ? "photos" : "files"} queued`}</span>
          {count > 1 && (
            <button type="button" onClick={onClearAll} className="text-blue-600 hover:text-blue-700 text-xs" data-testid="clear-all-selection">
              Clear all
            </button>
          )}
        </div>
        <div className="max-h-40 overflow-y-auto space-y-2 rounded-md border border-zinc-100 p-2 bg-zinc-50/60">
          {selectedFiles.map((f, idx) => (
            <div key={f.id} className="flex items-start gap-2" data-testid={`queued-file-${f.id}`}>
              <div className="relative shrink-0">
                <DriveThumbnail
                  fileId={f.id}
                  hasThumbnail={f.hasThumbnail !== false}
                  name={f.name}
                  className="w-12 h-8"
                />
                <span className="absolute -top-1 -left-1 flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold ring-2 ring-white">
                  {idx + 1}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-slate-900 truncate" title={f.name}>{f.name}</div>
                <div className="text-[10px] text-slate-500 tabular-nums">
                  {f.mimeType || ""}{f.size ? ` • ${humanSize(f.size)}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemove(f.id)}
                className="text-[10px] text-slate-400 hover:text-red-600 transition-colors shrink-0"
                data-testid={`remove-queued-${f.id}`}
                aria-label={`Remove ${f.name} from queue`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>

      <PlatformAndAccountPicker
        selectedPlatform={selectedPlatform}
        setSelectedPlatform={setSelectedPlatform}
        anyTikTok={anyTikTok}
        anyInstagram={anyInstagram}
        linkedAccounts={linkedAccounts}
        selectedAccountId={selectedAccountId}
        setSelectedAccountId={setSelectedAccountId}
      />

      {isCarousel && selectedPlatform === "tiktok" && (
        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="auto-music-toggle">
          <input
            type="checkbox"
            checked={autoMusic}
            onChange={(e) => setAutoMusic(e.target.checked)}
            className="rounded border-zinc-300"
          />
          Let TikTok auto-add background music
        </label>
      )}

      <div>
        <Label htmlFor="caption" className="text-slate-700">Caption <span className="text-slate-400 font-normal">(note only)</span></Label>
        <Textarea
          id="caption"
          rows={3}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Internal note kept in the log"
          data-testid="caption-input"
        />
        <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {isCarousel
              ? "Set the final caption inside the TikTok/Instagram app after the post lands in your inbox."
              : "In TikTok draft mode, the final caption and privacy are set inside the app after the video arrives."}
          </span>
        </div>
      </div>
    </>
  );
}

function PlatformAndAccountPicker({
  compact = false,
  selectedPlatform, setSelectedPlatform,
  anyTikTok, anyInstagram, linkedAccounts, selectedAccountId, setSelectedAccountId,
}) {
  const showTT = anyTikTok || selectedPlatform === "tiktok";
  const showIG = anyInstagram || selectedPlatform === "instagram";
  return (
    <div className={compact ? "space-y-2" : ""}>
      {!compact && <Label className="text-slate-700">Platform</Label>}
      <div className="flex gap-2" data-testid="platform-picker">
        {showTT && (
          <button
            type="button"
            onClick={() => setSelectedPlatform("tiktok")}
            disabled={!anyTikTok}
            className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
              selectedPlatform === "tiktok"
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : anyTikTok
                  ? "border-zinc-200 text-slate-700 hover:bg-zinc-50"
                  : "border-zinc-100 text-slate-400 cursor-not-allowed"
            }`}
            data-testid="platform-tiktok"
          >
            TikTok
          </button>
        )}
        {showIG && (
          <button
            type="button"
            onClick={() => setSelectedPlatform("instagram")}
            disabled={!anyInstagram}
            className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
              selectedPlatform === "instagram"
                ? "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700"
                : anyInstagram
                  ? "border-zinc-200 text-slate-700 hover:bg-zinc-50"
                  : "border-zinc-100 text-slate-400 cursor-not-allowed"
            }`}
            data-testid="platform-instagram"
          >
            Instagram
          </button>
        )}
      </div>
      {linkedAccounts.length === 0 ? (
        <div className="text-[11px] text-red-600 mt-1" data-testid="no-platform-accounts">
          No accounts have {selectedPlatform === "tiktok" ? "TikTok" : "Instagram"} linked yet. Open Integrations to link one.
        </div>
      ) : (
        <>
          {!compact && <Label htmlFor="account-select" className="text-slate-700 mt-2 block">Account</Label>}
          <Select value={selectedAccountId || undefined} onValueChange={setSelectedAccountId}>
            <SelectTrigger id={compact ? undefined : "account-select"} data-testid="account-select" className={compact ? "h-9 text-sm" : ""}>
              <SelectValue placeholder="Choose an account" />
            </SelectTrigger>
            <SelectContent>
              {linkedAccounts.map((a) => (
                <SelectItem key={a.id} value={a.id} data-testid={`account-option-${a.id}`}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}
    </div>
  );
}

function SendProgressBar({ progress, onDismiss, platform }) {
  if (!progress.active && progress.results.length === 0) return null;
  const done = !progress.active;
  const successCount = progress.results.filter((r) => r.status === "success").length;
  const failureCount = progress.results.filter((r) => r.status === "failure").length;
  const currentIndex = Math.min(progress.done + (done ? 0 : 1), progress.total);
  const percent = progress.total > 0 ? Math.round(((done ? progress.total : progress.done) / progress.total) * 100) : 0;
  const platformLabel = platform === "tiktok" ? "TikTok" : "Instagram";

  return (
    <div
      className="fixed z-40 right-3 left-3 sm:right-6 sm:left-auto sm:w-96"
      style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0px))" }}
      data-testid="send-progress-strip"
      role="status"
      aria-live="polite"
    >
      <div className="rounded-xl border border-zinc-200 bg-white shadow-lg overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="shrink-0">
            {done ? (
              failureCount === 0
                ? <CheckCircle2 className="w-5 h-5 text-emerald-600" data-testid="progress-icon-success" />
                : <CheckCircle2 className="w-5 h-5 text-amber-500" data-testid="progress-icon-mixed" />
            ) : (
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" data-testid="progress-icon-spinner" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="progress-title">
              {done
                ? (failureCount === 0
                    ? (progress.mode === "carousel" ? `Posted to ${platformLabel}` : (progress.total > 1 ? "All videos posted" : `Posted to ${platformLabel}`))
                    : "Finished with some errors")
                : (progress.mode === "carousel"
                    ? `Posting carousel to ${platformLabel}…`
                    : (progress.total > 1
                        ? `Posting ${currentIndex} of ${progress.total} to ${platformLabel}…`
                        : `Posting to ${platformLabel}…`))}
            </div>
            <div className="text-[11px] text-slate-500 truncate" data-testid="progress-subtitle">
              {done
                ? `${successCount} sent${failureCount ? `, ${failureCount} failed` : ""}. Finish captioning inside the app.`
                : (progress.current ? progress.current.name : "Preparing…")}
            </div>
          </div>
          {done && (
            <button
              type="button"
              onClick={onDismiss}
              className="shrink-0 text-slate-400 hover:text-slate-700 transition-colors p-1"
              data-testid="progress-close-button"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {progress.total > 1 && progress.mode !== "carousel" && (
          <div className="h-1 w-full bg-zinc-100">
            <div
              className={`h-full transition-all duration-300 ${done && failureCount === 0 ? "bg-emerald-500" : "bg-blue-600"}`}
              style={{ width: `${percent}%` }}
              data-testid="progress-bar"
            />
          </div>
        )}
        {done && progress.results.length > 0 && failureCount > 0 && (
          <div className="max-h-28 overflow-y-auto border-t border-zinc-100 divide-y divide-zinc-100" data-testid="progress-results">
            {progress.results.map((r, i) => (
              <div key={`${r.file.id}-${i}`} className="flex items-start gap-2 px-4 py-1.5 text-[11px]">
                {r.status === "success"
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                  : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate" title={r.file.name}>{r.file.name}</div>
                  {r.status === "failure" && (
                    <div className="text-red-600 truncate" title={r.summary}>{r.summary}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
